import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Check, X, Clock, FileText, CreditCard, Package, Truck, CircleCheck, Ban } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { getBuyerOrder } from '../../lib/api';

const STEP_KEYS = [
  { key: 'submitted', aliases: ['pending_approval'], icon: FileText, color: '#F59E0B' },
  { key: 'proforma_sent', aliases: [], icon: FileText, color: '#00D4FF' },
  { key: 'awaiting_wire', aliases: [], icon: CreditCard, color: '#8B5CF6' },
  { key: 'paid', aliases: ['payment_confirmed'], icon: Check, color: '#10B981' },
  { key: 'processing', aliases: [], icon: Package, color: '#3B82F6' },
  { key: 'shipped', aliases: [], icon: Truck, color: '#10B981' },
  { key: 'completed', aliases: [], icon: CircleCheck, color: '#10B981' },
];

function getStepIndex(status) {
  return STEP_KEYS.findIndex(s => s.key === status || s.aliases.includes(status));
}

function buildTimeline(order) {
  const isCancelled = order.status === 'cancelled';
  const history = order.status_history || [];
  const historyMap = {};
  for (const entry of history) {
    const s = entry.status === 'pending_approval' ? 'submitted' : entry.status;
    if (!historyMap[s]) historyMap[s] = entry.changed_at;
  }

  const currentIdx = isCancelled
    ? (() => {
        const nonCancelled = history.filter(h => h.status !== 'cancelled');
        if (!nonCancelled.length) return -1;
        const last = nonCancelled[nonCancelled.length - 1].status;
        return getStepIndex(last === 'pending_approval' ? 'submitted' : last);
      })()
    : getStepIndex(order.status === 'pending_approval' ? 'submitted' : order.status);

  return STEP_KEYS.map((step, idx) => ({
    ...step,
    reached: idx <= currentIdx,
    current: !isCancelled && idx === currentIdx,
    timestamp: historyMap[step.key] || null,
    disabled: isCancelled && idx > currentIdx,
  }));
}

const OrderTimeline = ({ order, t, language }) => {
  const isCancelled = order.status === 'cancelled';
  const steps = buildTimeline(order);
  const cancelledAt = (order.status_history || []).find(h => h.status === 'cancelled')?.changed_at;

  return (
    <div data-testid="order-timeline">
      {/* Desktop horizontal */}
      <div className="hidden md:block">
        <div className="flex items-start justify-between relative">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const label = t(`timeline.${step.key}`);
            return (
              <div key={step.key} className="flex flex-col items-center relative z-10 flex-1" data-testid={`timeline-step-${step.key}`}>
                <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 transition-all ${
                  step.reached && !step.disabled ? '' : 'border-white/10 bg-[#0A0A0A]'
                }`} style={step.reached && !step.disabled ? {
                  borderColor: step.current ? step.color : '#10B981',
                  backgroundColor: step.current ? `${step.color}20` : '#10B98120',
                  boxShadow: step.current ? `0 0 12px ${step.color}40` : 'none',
                } : {}}>
                  {step.reached && !step.disabled ? (
                    step.current
                      ? <Icon className="w-4 h-4" style={{ color: step.color }} />
                      : <Check className="w-4 h-4 text-[#10B981]" />
                  ) : (
                    <Icon className="w-4 h-4 text-white/20" />
                  )}
                </div>
                <div className={`mt-2 text-center ${step.reached && !step.disabled ? 'text-white' : 'text-white/25'}`}>
                  <div className="text-xs font-medium leading-tight">{label}</div>
                  {step.timestamp && (
                    <div className="text-[10px] text-white/40 mt-0.5">{new Date(step.timestamp).toLocaleDateString(language === 'es' ? 'es' : 'en')}</div>
                  )}
                </div>
                {idx < steps.length - 1 && (
                  <div className="absolute top-5 left-[calc(50%+20px)] right-[calc(-50%+20px)] h-0.5" style={{
                    backgroundColor: steps[idx + 1].reached && !steps[idx + 1].disabled ? '#10B981' : '#ffffff10',
                    width: 'calc(100% - 40px)',
                    left: '50%',
                    transform: 'translateX(20px)',
                  }} />
                )}
              </div>
            );
          })}
        </div>
        {isCancelled && (
          <div className="flex items-center justify-center gap-2 mt-4 py-2 bg-red-500/10 border border-red-500/20" data-testid="cancelled-badge">
            <Ban className="w-4 h-4 text-red-400" />
            <span className="text-red-400 font-medium text-sm">{t('orderDetail.orderCancelled')}</span>
            {cancelledAt && <span className="text-white/30 text-xs">{new Date(cancelledAt).toLocaleString(language === 'es' ? 'es' : 'en')}</span>}
          </div>
        )}
      </div>

      {/* Mobile vertical */}
      <div className="md:hidden">
        <div className="relative pl-8">
          <div className="absolute left-[15px] top-0 bottom-0 w-0.5 bg-white/10" />
          {steps.map((step) => {
            const Icon = step.icon;
            const label = t(`timeline.${step.key}`);
            return (
              <div key={step.key} className="relative flex items-start gap-3 pb-5" data-testid={`timeline-step-mobile-${step.key}`}>
                <div className="absolute left-[-17px] w-8 h-8 flex items-center justify-center rounded-full border-2 z-10" style={step.reached && !step.disabled ? {
                  borderColor: step.current ? step.color : '#10B981',
                  backgroundColor: step.current ? `${step.color}20` : '#10B98120',
                } : { borderColor: '#ffffff15', backgroundColor: '#0A0A0A' }}>
                  {step.reached && !step.disabled ? (
                    step.current
                      ? <Icon className="w-3.5 h-3.5" style={{ color: step.color }} />
                      : <Check className="w-3.5 h-3.5 text-[#10B981]" />
                  ) : (
                    <Icon className="w-3.5 h-3.5 text-white/20" />
                  )}
                </div>
                <div className={`pt-1 ${step.reached && !step.disabled ? '' : 'opacity-30'}`}>
                  <div className="text-sm font-medium">{label}</div>
                  {step.timestamp && (
                    <div className="text-xs text-white/40">{new Date(step.timestamp).toLocaleString(language === 'es' ? 'es' : 'en')}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {isCancelled && (
          <div className="flex items-center gap-2 py-2 px-3 bg-red-500/10 border border-red-500/20 mt-1" data-testid="cancelled-badge-mobile">
            <Ban className="w-4 h-4 text-red-400" />
            <span className="text-red-400 font-medium text-sm">{t('orderDetail.orderCancelled')}</span>
            {cancelledAt && <span className="text-white/30 text-xs ml-auto">{new Date(cancelledAt).toLocaleString(language === 'es' ? 'es' : 'en')}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default function BuyerOrderDetail() {
  const { id } = useParams();
  const { t, language } = useApp();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await getBuyerOrder(id);
        setOrder(res.data);
      } catch (err) {
        console.error('Failed to fetch order:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00D4FF]" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-white/60">{t('orderDetail.notFound')}</p>
          <Link to="/buyer" className="text-[#00D4FF] mt-4 inline-block">{t('common.backToDashboard')}</Link>
        </div>
      </div>
    );
  }

  const statusHistory = [...(order.status_history || [])].sort(
    (a, b) => new Date(a.changed_at) - new Date(b.changed_at)
  );

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="buyer-order-detail">
      <div className="max-w-4xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/buyer" className="text-white/60 hover:text-white" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-mono text-[#00D4FF]" data-testid="order-number">{order.order_number}</h1>
            <span className="text-white/50 text-sm">{new Date(order.created_at).toLocaleDateString(language === 'es' ? 'es' : 'en')}</span>
          </div>
        </div>

        {/* Order Progress Timeline */}
        <div className="card-dark p-6 mb-6" data-testid="timeline-card">
          <h2 className="text-lg font-semibold mb-5">{t('orderDetail.orderProgress')}</h2>
          <OrderTimeline order={order} t={t} language={language} />
        </div>

        {/* Fulfillment Timeline Notice */}
        <div className="bg-[#00D4FF]/5 border border-[#00D4FF]/15 p-4 mb-6 flex items-start gap-3" data-testid="fulfillment-notice">
          <Clock className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
          <p className="text-white/50 text-sm">
            {t('orderDetail.fulfillmentNotice')}
          </p>
        </div>

        {/* Items Table */}
        <div className="card-dark overflow-hidden mb-6" data-testid="order-items">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0A0A0A] border-b border-white/10">
                  <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">{t('cart.uniqueNum')}</th>
                  <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">{t('cart.brand')}</th>
                  <th className="p-4 text-left text-xs font-semibold text-white/60 uppercase">{t('cart.model')}</th>
                  <th className="p-4 text-right text-xs font-semibold text-white/60 uppercase">{t('cart.price')}</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item, idx) => (
                  <tr key={idx} className="border-b border-white/5">
                    <td className="p-4 font-mono text-[#00D4FF]">{item.unique_id}</td>
                    <td className="p-4">{item.oem}</td>
                    <td className="p-4 text-white/80">{item.model}</td>
                    <td className="p-4 text-right font-mono font-bold">${item.sale_price?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="p-4 bg-[#050505] border-t border-white/10 flex justify-between items-center">
            <span className="text-white/60">{order.items?.length || 0} {t('common.units')}</span>
            <span className="text-xl font-bold font-mono text-[#00D4FF]" data-testid="order-total">${order.total_amount?.toLocaleString()}</span>
          </div>
        </div>

        {/* Status Updates Log */}
        {statusHistory.length > 0 && (
          <div className="card-dark p-6" data-testid="status-updates-log">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#00D4FF]" />
              {t('orderDetail.statusUpdates')}
            </h2>
            <div className="space-y-2">
              {statusHistory.map((entry, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0" data-testid={`status-update-${idx}`}>
                  <div className="flex items-center gap-2">
                    {entry.status === 'cancelled' ? (
                      <X className="w-4 h-4 text-red-400" />
                    ) : (
                      <Check className="w-4 h-4 text-[#10B981]" />
                    )}
                    <span className="text-sm font-medium">{t(`status.${entry.status}`) || entry.status}</span>
                  </div>
                  <span className="text-white/40 text-xs">{new Date(entry.changed_at).toLocaleString(language === 'es' ? 'es' : 'en')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
